import { getStratoLogger } from '../../../core/sdk/sdk-logger.js';
import type { FormRuntime } from './createFormRuntime.js';

/**
 * Module-level registry of `<Form>` runtimes keyed by their `id`. It lets a
 * control rendered OUTSIDE a `<Form>` subtree associate with it via the native
 * `form="formId"` attribute — the same association the DOM offers, mirrored for
 * the runtime.
 *
 * The registry is observable: a control may render and look up its form before
 * the `<Form>` has registered (effect ordering across separate subtrees gives
 * no guarantee), so it subscribes and re-resolves once the form appears.
 * @internal
 */
const formsById = new Map<string, FormRuntime>();
const listenersById = new Map<string, Set<() => void>>();

function notify(id: string) {
  for (const listener of listenersById.get(id) ?? []) {
    listener();
  }
}

/** Registers a runtime under `id`; returns an unregister callback. @internal */
export function registerFormById(id: string, runtime: FormRuntime): () => void {
  const existing = formsById.get(id);
  if (existing !== undefined && existing !== runtime) {
    getStratoLogger('Form').warn(
      `Duplicate <Form id="${id}">. Form ids must be unique — controls and messages associated by id, and form-wide error routing, resolve to a single form. The previously registered form is being overwritten.`,
    );
  }
  formsById.set(id, runtime);
  notify(id);
  return () => {
    if (formsById.get(id) === runtime) {
      formsById.delete(id);
      notify(id);
    }
  };
}

/** Looks up a runtime registered under `id`. @internal */
export function getFormById(id: string): FormRuntime | undefined {
  return formsById.get(id);
}

/**
 * Subscribes to registration changes for `id`; the listener fires whenever a
 * form registers or unregisters under it. @internal
 */
export function subscribeToFormById(
  id: string,
  listener: () => void,
): () => void {
  let listeners = listenersById.get(id);
  if (listeners === undefined) {
    listeners = new Set();
    listenersById.set(id, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    // Drop the per-id bucket once its last listener leaves, so ids churned by
    // forms/controls mounting and unmounting don't leak empty Sets in the module map.
    if (listeners.size === 0) {
      listenersById.delete(id);
    }
  };
}
